import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { CharCounter } from '@/components/shared/CharCounter';
import type { KbProfileFormValues } from '@/hooks/useKbProfileForm';
import { CAPS } from '@/hooks/useKbProfileForm';
import { FieldHelp } from '@/components/onboarding/FieldHelp';

interface EssenceStepProps {
  form: UseFormReturn<KbProfileFormValues>;
}

export function EssenceStep({ form }: EssenceStepProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      {/* F069 #23 — both fields are optional; say so and nudge for quality */}
      <p className="text-xs text-muted-foreground">{t('onboarding.kbForm.step2.quality_nudge')}</p>
      <FormField
        control={form.control}
        name="one_liner"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-sm font-medium">
              <span className="flex items-center gap-1">
                <span>
                  {t('onboarding.kbForm.step2.one_liner_label')}{' '}
                  <span className="font-normal text-muted-foreground">
                    {t('onboarding.kbForm.step2.optional_badge')}
                  </span>
                </span>
                <FieldHelp baseKey="onboarding.kbForm.step2.one_liner" />
              </span>
            </FormLabel>
            <p className="text-xs text-muted-foreground">
              {t('onboarding.kbForm.step2.one_liner_hint')}
            </p>
            <FormControl>
              <Textarea
                rows={3}
                className="resize-none"
                placeholder={t('onboarding.kbForm.step2.one_liner_placeholder')}
                maxLength={CAPS.one_liner}
                {...field}
              />
            </FormControl>
            <CharCounter value={field.value ?? ''} max={CAPS.one_liner} />
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="ideal_customer"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-sm font-medium">
              <span className="flex items-center gap-1">
                <span>
                  {t('onboarding.kbForm.step2.ideal_customer_label')}{' '}
                  <span className="font-normal text-muted-foreground">
                    {t('onboarding.kbForm.step2.optional_badge')}
                  </span>
                </span>
                <FieldHelp baseKey="onboarding.kbForm.step2.ideal_customer" />
              </span>
            </FormLabel>
            <p className="text-xs text-muted-foreground">
              {t('onboarding.kbForm.step2.ideal_customer_hint')}
            </p>
            <FormControl>
              <Textarea
                rows={3}
                className="resize-none"
                placeholder={t('onboarding.kbForm.step2.ideal_customer_placeholder')}
                maxLength={CAPS.ideal_customer}
                {...field}
              />
            </FormControl>
            <CharCounter value={field.value ?? ''} max={CAPS.ideal_customer} />
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
